import { Component, OnInit, Input, OnDestroy, inject } from '@angular/core';
import {Validators, FormGroup, FormBuilder} from '@angular/forms';
import {throwError, Observable} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';

import {DropdownItem, NotificationService, TimeService, TimeValidators} from '@shared';

import {
    PatientTaskView,
    Patient,
    SkillLevelService,
    PatientTaskService,
    TaskUpdateService,
    NavigationService,
    TaskUpdate,
    ProblemInstanceService, BreakpointValue
} from 'planner-shared';
import {TaskEditService} from "../../../shared/services/task-edit/task-edit.service";

@Component({
    selector: 'app-patient-task-edit',
    templateUrl: './patient-task-edit.component.html',
    standalone: false
})
export class PatientTaskEditComponent implements OnInit, OnDestroy {
    private readonly formBuilder = inject(FormBuilder);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly skillLevelService = inject(SkillLevelService);
    private readonly patientTaskService = inject(PatientTaskService);
    private readonly taskUpdateService = inject(TaskUpdateService);
    private readonly notificationService = inject(NotificationService);
    private readonly navigationService = inject(NavigationService);
    private readonly timeService = inject(TimeService);
    private readonly taskEditService = inject(TaskEditService);

    @Input() public task: PatientTaskView;
    @Input() public patient: Patient;
    @Input() public showRoster: boolean;
    @Input() public breakPointValue: BreakpointValue;
    @Input() public coloringTaskFeatureEnabled: Observable<boolean>;

    public taskForm: FormGroup;
    public skillLevelDropdownItems$: Observable<DropdownItem[]>;
    public shiftTypeDropdownItems$: Observable<DropdownItem[]>;
    public numberOfEmployeesDropdownItems: DropdownItem[];
    public saving: boolean;
    protected readonly BreakpointValue = BreakpointValue;

    constructor() {
        this.numberOfEmployeesDropdownItems = <DropdownItem[]>[
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3}
        ];
    }

    ngOnInit(): void {
        this.taskForm = this.initTaskForm();
        this.skillLevelDropdownItems$ = this.skillLevelService.getSkillLevelDropdownItems();
        this.shiftTypeDropdownItems$ = this.problemInstanceService
            .getProblemInstance()
            .pipe(
                map(result =>
                    result.problemInstance.shiftTypes.map(
                        shiftType => <DropdownItem>{label: shiftType, value: shiftType}
                    )
                )
            );
    }

    ngOnDestroy(): void {
        setTimeout(() => this.save());
    }

    public save(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        if (!this.taskForm || this.taskForm.pristine || this.taskForm.invalid || this.saving) {
            return;
        }

        this.saving = true;
        this.patient.taskUpdating = true;
        this.task.updating = true;
        this.navigationService.disableNextButton(true);

        const patient = this.patient;
        const taskUpdate = <TaskUpdate>{
            taskId: this.task.taskId,
            startDate: this.task.fromDate,
            startTime: this.timeService.convertTime(this.taskForm.value.fromTime),
            endDate: this.task.toDate,
            endTime: this.timeService.convertTime(this.taskForm.value.toTime),
            duration: this.taskForm.value.duration,
            shiftType: this.taskForm.value.shiftType,
            skillRequirement: this.taskForm.value.skillRequirement,
            numberOfEmployees: this.taskForm.value.numberOfEmployees,
            permanent: this.taskForm.value.permanent,
            isActive: this.taskForm.value.isActive
        };

        this.taskUpdateService
            .updatePatientTask(taskUpdate)
            .pipe(
                switchMap(result =>
                    this.problemInstanceService.getProblemInstance().pipe(
                        tap(problemInstanceResult => {
                            problemInstanceResult.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                                result.numberOfTasksWithoutMainResponsibles;
                            problemInstanceResult.problemInstance.taskWorkShiftCompatibility =
                                result.taskWorkShiftCompatibility;
                        })
                    )
                ),
                catchError(() => throwError('Update patient task failed.')),
                finalize(() => {
                    this.saving = false;
                    this.task.editing = false;
                    this.task.updating = false;
                    this.patient.taskUpdating = false;
                    this.taskForm.markAsPristine();
                    this.patientTaskService.patientTaskEditStateChanged(patient);
                    this.navigationService.disableNextButton(false);
                })
            )
            .subscribe(() => {
                this.patientTaskService.mapTaskFromForm(this.taskForm, this.task);
                this.patientTaskService.updateProblemInstanceTask(this.task);
                this.notificationService.success('TASK_UPDATED', null, {id: this.task.taskId});
            });
    }

    public getTaskDurationError(taskForm: FormGroup): string {
        return this.taskEditService.getTaskDurationError(taskForm);
    }

    public revert(event: Event): void {
        event.stopPropagation();
        if (this.taskForm.dirty) {
            this.taskForm.patchValue(this.task);
            this.taskForm.markAsPristine();
        }
    }

    public timeChanged(): void {
        this.taskForm.controls.duration.updateValueAndValidity();
    }

    public clickedOutside(event?: Event): void {
        if (this.taskForm.dirty) {
            this.save(event);
        } else {
            this.task.editing = false;
            this.patientTaskService.patientTaskEditStateChanged();
        }
    }

    public getHiddenInitials(task: PatientTaskView): string {
        return task.mainResponsibleEmployees
            .slice(3, task.mainResponsibleEmployees.length)
            .map(i => i.initials)
            .join();
    }

    private initTaskForm(): FormGroup {
        return this.formBuilder.group(
            {
                fromTime: [
                    this.task.fromTime,
                    Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5)])
                ],
                toTime: [
                    this.task.toTime,
                    Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5)])
                ],
                duration: [
                    this.task.duration,
                    Validators.compose([
                        Validators.required,
                        Validators.min(1),
                        this.patientTaskService.taskDurationValidator('fromTime', 'toTime')
                    ])
                ],
                shiftType: [this.task.shiftType, Validators.required],
                skillRequirement: [this.task.skillRequirement, Validators.required],
                numberOfEmployees: [
                    this.task.numberOfEmployees,
                    Validators.compose([Validators.required, Validators.min(1)])
                ],
                permanent: [this.task.permanent, Validators.required],
                isActive: [this.task.isActive, Validators.required]
            },
            {
                validators: TimeValidators.dateAndTimeGreaterThanOrEqual(
                    this.task.fromDate,
                    'fromTime',
                    this.task.toDate,
                    'toTime'
                )
            }
        );
    }
}
